import { useCallback, useEffect, useRef, useState } from 'react';
import { FeedTabs } from '../../components/FeedTabs';
import { ReferralCard } from '../../components/ReferralCard';
import { MyHeader } from '../../components/MyHeader';
import { MiniProgressBar } from '../../components/MiniProgressBar';
import { BottomSheet } from '../../components/BottomSheet';
import { InitDiagnosticsScreen } from '../../components/InitDiagnosticsScreen';
import { ErrorMessage, ApiUnavailablePanel } from '../../components/Feedback';
import { IconPlus, IconBolt, IconMail, IconChevronRight } from '../../components/icons';
import { BrandedSplashScreen } from '../../components/BrandedSplashScreen';
import { useMyProjectsData } from './useMyProjectsData';
import { useProfile } from '../../hooks/useProfile';
import { useStreak } from '../../hooks/useStreak';
import { MyProjectCard } from './components/MyProjectCard';
import { EmptyProjectsState } from './components/EmptyProjectsState';
import { recordTapFromReactEvent } from '../../lib/tapTracker';
import { isLocalAppEnv } from '../../lib/runtimeEnv';
import { useAppStore } from '../../store/useAppStore';
import { haptic } from '../../utils/haptic';
import {
  approveRequest,
  createRequest,
  deleteRequest,
  fetchAdminRequests,
  getRequest,
  fetchMyRequests,
  isModeratorOrAdmin,
  needsFixRequest,
  rejectRequest,
  submitRequest,
  updateRequest,
  type CreateRequestPayload,
  type RequestDetail,
  type RequestListItem,
} from '../../lib/api';
import { useBackButton } from '../../hooks/useBackButton';
import { useClosingConfirmation } from '../../hooks/useClosingConfirmation';
import { useDisableSwipes } from '../../hooks/useSwipeBehavior';
import { useMainButton } from '../../hooks/useMainButton';
import { RequestCard } from '../requests/RequestCard';
import { CreateRequestForm } from '../requests/CreateRequestForm';
import { RequestDetailView } from '../requests/RequestDetailView';
import { SkeletonRequestCard } from '../../components/SkeletonRequestCard';
import { Reveal } from '../../components/ui/Reveal';

const tg = window.Telegram?.WebApp;
const MIN_INITIAL_SPLASH_MS = 6000;
let initialSplashCompleted = false;

function requestToFormValues(request: Pick<RequestDetail, 'type' | 'title' | 'task' | 'budget' | 'deadline' | 'format' | 'what_matters' | 'tools' | 'urgent'>): CreateRequestPayload {
  return {
    type: request.type,
    title: request.title,
    description: request.task,
    budget: request.budget,
    deadline: request.deadline,
    format: request.format,
    what_matters: [],
    tools: request.tools,
    urgent: request.urgent,
  };
}

function MyRequestsFormScreen({
  mode,
  initialValues,
  onSubmit,
  onCancel,
}: {
  mode: 'create' | 'edit';
  initialValues?: Partial<CreateRequestPayload> | null;
  onSubmit: (payload: CreateRequestPayload) => Promise<void>;
  onCancel: () => void;
}) {
  const formRef = useRef<HTMLFormElement>(null);
  const [submitting, setSubmitting] = useState(false);

  useBackButton(onCancel, true);
  useClosingConfirmation(true);
  useDisableSwipes(true);

  useMainButton({
    text: '',
    onClick: () => {},
    visible: false,
    disabled: true,
    loading: false,
  });

  const handleSubmit = async (payload: CreateRequestPayload) => {
    setSubmitting(true);
    try {
      await onSubmit(payload);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="requests-screen">
      <FeedTabs />
      <header className="form-header">
        <button type="button" className="form-header-back" onClick={onCancel} aria-label="Назад">
          <IconChevronRight size={20} style={{ transform: 'rotate(180deg)' }} />
        </button>
        <span className="form-header-title">{mode === 'edit' ? 'Редактирование заявки' : 'Новая заявка'}</span>
        <span className="form-header-saved">{mode === 'edit' ? 'Изменения' : 'Черновик'}</span>
      </header>
      <main className="main">
        <div className="container">
          <CreateRequestForm
            formRef={formRef}
            mode={mode}
            initialValues={initialValues}
            submitLabel={mode === 'edit' ? 'Сохранить изменения' : 'Сохранить черновик'}
            hideSubmitButton={false}
            onSubmit={handleSubmit}
            onCancel={onCancel}
            isSubmitting={submitting}
          />
        </div>
      </main>
    </div>
  );
}

export function MyProjectsScreen() {
  const showLocalDiagnostics = isLocalAppEnv();
  const {
    apiBaseUrl,
    projects,
    loading,
    bootWatchdogFired,
    error,
    errorDetails,
    lastFetchErrorInfo,
    apiUnavailable,
    loadProjects,
    handleOpen,
    handleEdit,
    handleSubmit,
    handleWithdraw,
    handleDelete,
    createProjectAndOpenWizard,
  } = useMyProjectsData();

  const { profile } = useProfile();
  const { streak } = useStreak();
  const setScreen = useAppStore((s) => s.setScreen);
  const setViewProfileUserId = useAppStore((s) => s.setViewProfileUserId);
  const addNotification = useAppStore((s) => s.addNotification);
  const screen = useAppStore((s) => s.screen);
  const activeTab = useAppStore((s) => s.activeTab);
  const currentUser = useAppStore((s) => s.currentUser);
  const isAdmin = Boolean(currentUser?.is_admin);
  const canModerateRequests = isModeratorOrAdmin(currentUser);
  const showAdminModeration = canModerateRequests;

  const [createSheetOpen, setCreateSheetOpen] = useState(false);
  const [myRequests, setMyRequests] = useState<RequestListItem[]>([]);
  const [myRequestsLoading, setMyRequestsLoading] = useState(false);
  const [myRequestsError, setMyRequestsError] = useState<string | null>(null);
  const [adminRequests, setAdminRequests] = useState<RequestListItem[]>([]);
  const [adminRequestsLoading, setAdminRequestsLoading] = useState(false);
  const [requestBusyId, setRequestBusyId] = useState<string | null>(null);
  const [selectedOwnRequestId, setSelectedOwnRequestId] = useState<string | null>(null);
  const [requestFormState, setRequestFormState] = useState<{
    mode: 'create' | 'edit';
    requestId?: string;
    initialValues?: Partial<CreateRequestPayload> | null;
    returnToDetail?: boolean;
  } | null>(null);
  const [minSplashElapsed, setMinSplashElapsed] = useState(initialSplashCompleted);
  const emptyRespondedIds = useRef(new Set<string>()).current;

  const user = tg?.initDataUnsafe?.user as { photo_url?: string; first_name?: string } | undefined;
  const showFab = activeTab === 'my' && screen === 'list';

  useEffect(() => {
    if (initialSplashCompleted) return;
    const timer = window.setTimeout(() => {
      initialSplashCompleted = true;
      setMinSplashElapsed(true);
    }, MIN_INITIAL_SPLASH_MS);
    return () => window.clearTimeout(timer);
  }, []);

  const shouldShowInitialSplash = !initialSplashCompleted;

  const closeCreateSheet = () => setCreateSheetOpen(false);
  const openCreateSheet = () => {
    haptic.tap();
    setCreateSheetOpen(true);
  };

  const loadMyRequests = useCallback(async () => {
    setMyRequestsLoading(true);
    try {
      const next = await fetchMyRequests();
      setMyRequests(next);
      setMyRequestsError(null);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Не удалось загрузить заявки';
      setMyRequestsError(message);
      addNotification(`Заявки не обновились: ${message}`);
    } finally {
      setMyRequestsLoading(false);
    }
  }, [addNotification]);

  const loadAdminRequests = useCallback(async () => {
    if (!showAdminModeration) return;
    setAdminRequestsLoading(true);
    try {
      setAdminRequests(await fetchAdminRequests('submitted'));
    } finally {
      setAdminRequestsLoading(false);
    }
  }, [showAdminModeration]);

  useEffect(() => {
    if (loading) return;
    void loadMyRequests();
  }, [loading, loadMyRequests]);

  useEffect(() => {
    if (loading || !showAdminModeration) {
      setAdminRequests([]);
      return;
    }
    void loadAdminRequests();
  }, [loading, showAdminModeration, loadAdminRequests]);

  const handleCreateProject = () => {
    closeCreateSheet();
    void createProjectAndOpenWizard();
  };

  const handleCreateRequest = () => {
    closeCreateSheet();
    setRequestFormState({ mode: 'create', initialValues: null, returnToDetail: false });
  };

  const handleCreateRequestSubmit = useCallback(
    async (payload: CreateRequestPayload) => {
      try {
        await createRequest(payload);
        addNotification('Черновик заявки создан');
        setRequestFormState(null);
        await loadMyRequests();
      } catch (e) {
        addNotification(e instanceof Error ? e.message : 'Не удалось создать заявку');
      }
    },
    [addNotification, loadMyRequests]
  );

  const openEditRequestForm = useCallback(
    async (requestId: string, returnToDetail = false) => {
      setRequestBusyId(requestId);
      try {
        const request = await getRequest(requestId);
        if (!request) {
          addNotification('Заявка не найдена');
          return;
        }
        setRequestFormState({
          mode: 'edit',
          requestId,
          initialValues: requestToFormValues(request),
          returnToDetail,
        });
      } catch (e) {
        addNotification(e instanceof Error ? e.message : 'Не удалось открыть редактирование заявки');
      } finally {
        setRequestBusyId(null);
      }
    },
    [addNotification]
  );

  const handleEditRequestSubmit = useCallback(
    async (payload: CreateRequestPayload) => {
      if (!requestFormState?.requestId) return;
      try {
        await updateRequest(requestFormState.requestId, payload);
        addNotification('Заявка обновлена');
        const requestId = requestFormState.requestId;
        const shouldReturnToDetail = Boolean(requestFormState.returnToDetail);
        setRequestFormState(null);
        await loadMyRequests();
        if (showAdminModeration) {
          await loadAdminRequests();
        }
        if (shouldReturnToDetail) {
          setSelectedOwnRequestId(requestId);
        }
      } catch (e) {
        addNotification(e instanceof Error ? e.message : 'Не удалось сохранить заявку');
      }
    },
    [addNotification, loadAdminRequests, loadMyRequests, requestFormState, showAdminModeration]
  );

  const handleSubmitMyRequest = useCallback(
    async (id: string) => {
      setRequestBusyId(id);
      try {
        await submitRequest(id);
        addNotification('Заявка отправлена на модерацию');
        await loadMyRequests();
        if (showAdminModeration) {
          await loadAdminRequests();
        }
      } catch (e) {
        addNotification(e instanceof Error ? e.message : 'Не удалось отправить заявку');
      } finally {
        setRequestBusyId(null);
      }
    },
    [addNotification, showAdminModeration, loadAdminRequests, loadMyRequests]
  );

  const handleDeleteRequest = useCallback(
    async (id: string) => {
      const confirmed = window.confirm('Удалить заявку? Это действие нельзя отменить.');
      if (!confirmed) return;
      setRequestBusyId(id);
      try {
        await deleteRequest(id);
        addNotification('Заявка удалена');
        if (selectedOwnRequestId === id) {
          setSelectedOwnRequestId(null);
        }
        if (requestFormState?.requestId === id) {
          setRequestFormState(null);
        }
        await loadMyRequests();
        if (showAdminModeration) {
          await loadAdminRequests();
        }
      } catch (e) {
        addNotification(e instanceof Error ? e.message : 'Не удалось удалить заявку');
      } finally {
        setRequestBusyId(null);
      }
    },
    [addNotification, showAdminModeration, loadAdminRequests, loadMyRequests, selectedOwnRequestId, requestFormState]
  );

  const handleApprove = useCallback(
    async (id: string) => {
      setRequestBusyId(id);
      try {
        await approveRequest(id);
        addNotification('Заявка одобрена');
        await Promise.all([loadAdminRequests(), loadMyRequests()]);
      } catch (e) {
        addNotification(e instanceof Error ? e.message : 'Не удалось одобрить заявку');
      } finally {
        setRequestBusyId(null);
      }
    },
    [addNotification, loadAdminRequests, loadMyRequests]
  );

  const handleReject = useCallback(
    async (id: string) => {
      const reason = window.prompt('Причина отклонения');
      if (!reason || !reason.trim()) return;
      setRequestBusyId(id);
      try {
        await rejectRequest(id, reason.trim());
        addNotification('Заявка отклонена');
        await Promise.all([loadAdminRequests(), loadMyRequests()]);
      } catch (e) {
        addNotification(e instanceof Error ? e.message : 'Не удалось отклонить заявку');
      } finally {
        setRequestBusyId(null);
      }
    },
    [addNotification, loadAdminRequests, loadMyRequests]
  );

  const handleNeedsFix = useCallback(
    async (id: string) => {
      const comment = window.prompt('Комментарий для доработки');
      if (!comment || !comment.trim()) return;
      setRequestBusyId(id);
      try {
        await needsFixRequest(id, comment.trim());
        addNotification('Заявка возвращена на доработку');
        await Promise.all([loadAdminRequests(), loadMyRequests()]);
      } catch (e) {
        addNotification(e instanceof Error ? e.message : 'Не удалось вернуть на доработку');
      } finally {
        setRequestBusyId(null);
      }
    },
    [addNotification, loadAdminRequests, loadMyRequests]
  );

  const openAuthorProfile = useCallback(
    (authorId: number) => {
      if (!authorId) return;
      setViewProfileUserId(authorId);
      setScreen('user-profile');
    },
    [setScreen, setViewProfileUserId]
  );

  const handleOwnRequestDetailBack = useCallback(() => {
    setSelectedOwnRequestId(null);
  }, []);

  if (selectedOwnRequestId) {
    const selectedRequest = myRequests.find((item) => item.id === selectedOwnRequestId) ?? null;
    const detailActionLabel =
      selectedRequest?.status === 'draft' || selectedRequest?.status === 'needs_fix' || selectedRequest?.status === 'rejected'
        ? 'На модерацию'
        : null;
    return (
      <RequestDetailView
        requestId={selectedOwnRequestId}
        onBack={handleOwnRequestDetailBack}
        onRespond={() => {}}
        respondedIds={emptyRespondedIds}
        onOpenAuthorProfile={openAuthorProfile}
        hideRespondButton
        ownerActionLabel={detailActionLabel}
        onOwnerAction={detailActionLabel ? handleSubmitMyRequest : undefined}
        onEdit={(id) => {
          void openEditRequestForm(id, true);
        }}
        onDelete={handleDeleteRequest}
        actionDisabled={requestBusyId === selectedOwnRequestId}
      />
    );
  }

  if (requestFormState) {
    return (
      <MyRequestsFormScreen
        mode={requestFormState.mode}
        initialValues={requestFormState.initialValues}
        onSubmit={requestFormState.mode === 'edit' ? handleEditRequestSubmit : handleCreateRequestSubmit}
        onCancel={() => {
          const returnToDetail = requestFormState.returnToDetail && requestFormState.requestId;
          const requestId = requestFormState.requestId;
          setRequestFormState(null);
          if (returnToDetail && requestId) {
            setSelectedOwnRequestId(requestId);
          }
        }}
      />
    );
  }

  if (shouldShowInitialSplash && (loading || !minSplashElapsed)) {
    if (loading && bootWatchdogFired && minSplashElapsed) {
      if (showLocalDiagnostics) {
        return (
          <InitDiagnosticsScreen
            apiBaseUrl={import.meta.env.VITE_API_PUBLIC_URL || ''}
            onRetry={loadProjects}
          />
        );
      }
      return <ErrorMessage message="Не удалось завершить инициализацию. Попробуйте ещё раз." onRetry={loadProjects} />;
    }
    return <BrandedSplashScreen />;
  }

  if (loading && projects.length === 0) {
    if (bootWatchdogFired) {
      if (showLocalDiagnostics) {
        return (
          <InitDiagnosticsScreen
            apiBaseUrl={import.meta.env.VITE_API_PUBLIC_URL || ''}
            onRetry={loadProjects}
          />
        );
      }
      return <ErrorMessage message="Не удалось завершить инициализацию. Попробуйте ещё раз." onRetry={loadProjects} />;
    }
    return <div className="loading"><div className="spinner" /><p>Загрузка...</p></div>;
  }

  if (apiUnavailable && apiBaseUrl) {
    return (
      <ApiUnavailablePanel
        apiBaseUrl={apiBaseUrl}
        errorInfo={apiUnavailable}
        onRetry={loadProjects}
      />
    );
  }

  if (error) {
    return (
      <ErrorMessage
        message={error}
        details={errorDetails}
        fetchInfo={lastFetchErrorInfo}
        onRetry={loadProjects}
      />
    );
  }

  return (
    <div
      className="my-projects-screen"
      onPointerDownCapture={(e) => recordTapFromReactEvent('pointerdown', e.nativeEvent)}
      onClickCapture={(e) => recordTapFromReactEvent('click', e.nativeEvent)}
    >
      <FeedTabs />
      <MyHeader
        photoUrl={user?.photo_url}
        firstName={user?.first_name ?? ''}
        projectCount={projects.length}
        streakDays={streak}
        level={profile?.level ?? 0}
        onProfileClick={() => setScreen('profile')}
      />
      <MiniProgressBar completeness={profile?.profile_completeness ?? 0} />
      <ReferralCard variant="strip" />
      <main className="my-projects-main my-content">
        <div className="my-projects-container">
          {projects.length === 0 ? (
            <EmptyProjectsState onCreateClick={createProjectAndOpenWizard} />
          ) : (
            projects.map((project) => (
              <MyProjectCard
                key={project.id}
                project={project}
                onOpen={handleOpen}
                onEdit={handleEdit}
                onSubmit={handleSubmit}
                onWithdraw={handleWithdraw}
                onDelete={handleDelete}
              />
            ))
          )}

          <section className="my-requests-section">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
              <h2 className="section-title" style={{ marginBottom: 0 }}>Мои заявки</h2>
              <button type="button" className="btn btn-compact btn-primary" onClick={() => setRequestFormState({ mode: 'create', initialValues: null, returnToDetail: false })}>
                Создать заявку
              </button>
            </div>
            {myRequestsError && (
              <p className="my-requests-placeholder" style={{ color: '#fda4af' }}>
                Не удалось обновить список заявок: {myRequestsError}. Показываем последние загруженные данные.
              </p>
            )}
            {myRequestsLoading ? (
              <>
                <SkeletonRequestCard />
                <SkeletonRequestCard />
              </>
            ) : myRequests.length === 0 ? (
              <p className="my-requests-placeholder">У вас пока нет заявок.</p>
            ) : (
              <div className="requests-list" style={{ marginTop: 12 }}>
                {myRequests.map((item, i) => (
                  <Reveal key={item.id} delay={i * 40}>
                    <>
                      <RequestCard
                        request={item}
                        onClick={() => {
                          setSelectedOwnRequestId(item.id);
                        }}
                        hideDefaultAction
                      />
                      <div className="request-owner-actions">
                        <button
                          type="button"
                          className="btn btn-compact"
                          onClick={() => {
                            void openEditRequestForm(item.id, false);
                          }}
                          disabled={requestBusyId === item.id}
                        >
                          Редактировать
                        </button>
                        <button
                          type="button"
                          className="btn btn-compact"
                          onClick={() => handleDeleteRequest(item.id)}
                          disabled={requestBusyId === item.id}
                        >
                          Удалить
                        </button>
                        {(item.status === 'draft' || item.status === 'needs_fix' || item.status === 'rejected') && (
                          <button
                            type="button"
                            className="btn btn-compact btn-primary"
                            onClick={() => handleSubmitMyRequest(item.id)}
                            disabled={requestBusyId === item.id}
                          >
                            На модерацию
                          </button>
                        )}
                      </div>
                    </>
                  </Reveal>
                ))}
              </div>
            )}
          </section>

          {showAdminModeration && (
            <section className="my-requests-section">
              <h2 className="section-title">Модерация: заявки на проверке</h2>
              {adminRequestsLoading ? (
                <>
                  <SkeletonRequestCard />
                  <SkeletonRequestCard />
                </>
              ) : adminRequests.length === 0 ? (
                <p className="my-requests-placeholder">Очередь модерации пуста.</p>
              ) : (
                <div className="requests-list">
                  {adminRequests.map((item, i) => (
                    <Reveal key={item.id} delay={i * 40}>
                      <>
                        <RequestCard
                          request={item}
                          hideDefaultAction
                        />
                        <div className="request-owner-actions">
                          <button
                            type="button"
                            className="btn btn-compact btn-primary"
                            onClick={() => handleApprove(item.id)}
                            disabled={requestBusyId === item.id}
                          >
                            Одобрить
                          </button>
                          <button
                            type="button"
                            className="btn btn-compact"
                            onClick={() => handleNeedsFix(item.id)}
                            disabled={requestBusyId === item.id}
                          >
                            Needs fix
                          </button>
                          <button
                            type="button"
                            className="btn btn-compact"
                            onClick={() => handleReject(item.id)}
                            disabled={requestBusyId === item.id}
                          >
                            Reject
                          </button>
                          {isAdmin && (
                            <button
                              type="button"
                              className="btn btn-compact"
                              onClick={() => handleDeleteRequest(item.id)}
                              disabled={requestBusyId === item.id}
                            >
                              Удалить
                            </button>
                          )}
                        </div>
                      </>
                    </Reveal>
                  ))}
                </div>
              )}
            </section>
          )}
        </div>
      </main>
      {showFab && (
        <button
          type="button"
          className="my-fab"
          onClick={openCreateSheet}
          aria-label="Создать"
        >
          <IconPlus />
        </button>
      )}
      <BottomSheet open={createSheetOpen} onClose={closeCreateSheet}>
        <div className="create-sheet-title">Создать</div>
        <div className="create-sheet-subtitle">Что хочешь добавить?</div>
        <button type="button" className="create-option" onClick={handleCreateProject}>
          <div className="create-option-icon create-option-icon-purple">
            <IconBolt size={20} />
          </div>
          <div className="create-option-text">
            <div className="create-option-title">Проект</div>
            <div className="create-option-desc">Покажи что навайбкодил</div>
          </div>
          <IconChevronRight size={18} />
        </button>
        <button type="button" className="create-option" onClick={handleCreateRequest}>
          <div className="create-option-icon create-option-icon-purple">
            <IconMail size={20} />
          </div>
          <div className="create-option-text">
            <div className="create-option-title">Заявку</div>
            <div className="create-option-desc">Найди разработчика или партнёра</div>
          </div>
          <IconChevronRight size={18} />
        </button>
        <button type="button" className="create-cancel" onClick={closeCreateSheet}>
          Отмена
        </button>
      </BottomSheet>
    </div>
  );
}

export { useMyProjectsData };
